import { useState, useEffect, useCallback, useMemo } from 'react';
import ChangeOrderPricingFields from '@/components/feature/ChangeOrderPricingFields';
import {
  type ChangeOrderPricingMode,
  type ChangeOrderPricingType,
  persistFormPricing,
} from '@/lib/changeOrderPricing';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { useManagementProjectsLite } from '@/hooks/useManagementProjectsLite';
import FileUploader, { type UploadedFileRecord } from './FileUploader';
import ChangeOrderAttachmentList from './ChangeOrderAttachmentList';
import AssignSubcontractorPanel from './AssignSubcontractorPanel';
import {
  getSuggestedCoNumber,
  normalizeCoNumber,
  resolveCoNumberFields,
  validateCoNumberUnique,
  emitChangeOrdersSync,
  unlinkChangeOrderAttachment,
} from '@/lib/changeOrderUtils';

interface SubcontractorOption {
  id: string;
  full_name: string | null;
  company_name: string | null;
  email: string | null;
}

interface ProjectOption {
  id: string;
  project_name: string;
  project_code: string;
  site_address: string | null;
  status: string;
  client_user_id: string | null;
  client_name: string | null;
}

type CoTarget = 'internal' | 'subcontractor' | 'client';

interface Props {
  show: boolean;
  onClose: () => void;
  projectId: string;
  onSuccess?: () => void;
  allowProjectChange?: boolean;
  source?: string;
}

async function fetchProjectWithClient(projectId: string): Promise<ProjectOption | null> {
  const { data: proj, error: projErr } = await supabase
    .from('jphph_projects')
    .select('id, project_name, site_address, status, client_user_id, client_name')
    .eq('id', projectId)
    .maybeSingle();
  if (projErr || !proj) {
    return null;
  }
  let clientUserId = (proj.client_user_id as string | null) || null;
  let clientName = (proj.client_name as string | null) || null;
  if (!clientUserId) {
    const { data: members } = await supabase
      .from('jphph_project_members')
      .select('user_id')
      .eq('project_id', projectId)
      .eq('member_role', 'client')
      .limit(1);
    clientUserId = members && members.length > 0 ? (members[0].user_id as string) : null;
  }
  if (clientUserId && !clientName) {
    const { data: profile } = await supabase
      .from('jphph_profiles')
      .select('full_name, company_name')
      .eq('id', clientUserId)
      .maybeSingle();
    clientName = (profile?.full_name as string | null) || (profile?.company_name as string | null) || null;
  }
  return {
    id: proj.id,
    project_name: proj.project_name as string,
    project_code: proj.id.slice(0, 6).toUpperCase(),
    site_address: (proj.site_address as string | null) || null,
    status: (proj.status as string) || 'active',
    client_user_id: clientUserId,
    client_name: clientName,
  };
}

async function loadSubcontractorsForProject(projectId: string): Promise<SubcontractorOption[]> {

  try {
    const { data: efData, error: efErr } = await supabase.functions.invoke('jphph-subcontractor-assignments', {
      body: { action: 'list', project_id: projectId },
    });

    if (!efErr && efData && typeof efData === 'object') {
      const subsArr = (efData as Record<string, unknown>).subcontractors;
      if (Array.isArray(subsArr)) {

        return subsArr as SubcontractorOption[];
      }
    }
  } catch (efFail) {
    // fallback to next tier
  }
  try {
    const { data: members, error: memberErr } = await supabase
      .from('jphph_project_members')
      .select('user_id')
      .eq('project_id', projectId)
      .eq('member_role', 'subcontractor');
    if (memberErr) throw memberErr;
    const userIds = (members || []).map((m) => m.user_id as string);
    if (userIds.length > 0) {
      const { data: profiles, error: profileErr } = await supabase
        .from('jphph_profiles')
        .select('id, full_name, company_name, email')
        .in('id', userIds);
      if (profileErr) throw profileErr;
      const subs = (profiles || []) as SubcontractorOption[];

      return subs;
    }
  } catch (localErr) {
    // fallback to next tier
  }
  try {
    const url = `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/jphph-subcontractor-assignments`;
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;
    const anonKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY as string | undefined;
    if (token && anonKey) {
      const resp = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          apikey: anonKey,
        },
        body: JSON.stringify({ action: 'list', project_id: projectId }),
      });
      const rawData = await resp.json().catch(() => null);
      if (rawData && Array.isArray(rawData.subcontractors)) {

        return rawData.subcontractors as SubcontractorOption[];
      }
    }
  } catch (rawErr) {
    // fallback to next tier
  }

  return [];
}

export default function NewChangeOrderModal({
  show, onClose, projectId: initialProjectId, onSuccess, allowProjectChange = false, source = 'unknown',
}: Props) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { projects: selectProjects, loading: selectProjectsLoading, error: selectProjectsError, refresh: refreshSelectProjects } = useManagementProjectsLite();

  const [selectedProjectId, setSelectedProjectId] = useState(initialProjectId || '');
  const [selectedProject, setSelectedProject] = useState<ProjectOption | null>(null);
  const [projectLoading, setProjectLoading] = useState(false);

  const [subcontractors, setSubcontractors] = useState<SubcontractorOption[]>([]);
  const [subcontractorsLoading, setSubcontractorsLoading] = useState(false);
  const [showAssignPanel, setShowAssignPanel] = useState(false);

  const [coTarget, setCoTarget] = useState<CoTarget>('internal');
  const [toUserId, setToUserId] = useState('');

  const [title, setTitle] = useState('');
  const [coNumber, setCoNumber] = useState('');
  const [description, setDescription] = useState('');
  const [clientDescription, setClientDescription] = useState('');
  const [reason, setReason] = useState('');
  const [currentFinish, setCurrentFinish] = useState('');
  const [newFinish, setNewFinish] = useState('');
  const [scheduleImpact, setScheduleImpact] = useState('');
  const [builderCost, setBuilderCost] = useState('');
  const [clientPrice, setClientPrice] = useState('');
  const [pricingMode, setPricingMode] = useState<ChangeOrderPricingMode>('manual');
  const [pricingType, setPricingType] = useState<ChangeOrderPricingType>('addition');
  const [clientPriceError, setClientPriceError] = useState<string | null>(null);
  const [builderCostError, setBuilderCostError] = useState<string | null>(null);
  const [internalNote, setInternalNote] = useState('');
  const [requiresClientApproval, setRequiresClientApproval] = useState(true);

  const [uploadedFiles, setUploadedFiles] = useState<UploadedFileRecord[]>([]);
  const [showFileUploader, setShowFileUploader] = useState(false);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Debug: track hook state changes


  // Auto-switch to internal if client approval selected but no client assigned
  useEffect(() => {
    if (coTarget === 'client' && selectedProject && !selectedProject.client_user_id) {
      setCoTarget('internal');
      setRequiresClientApproval(false);
      setError('');
    }
  }, [coTarget, selectedProject]);

  // ── State reset on open / close ──
  useEffect(() => {
    if (!show) {
      resetForm();
      return;
    }

    setTitle('');
    setCoNumber('');
    setDescription('');
    setClientDescription('');
    setReason('');
    setCurrentFinish('');
    setNewFinish('');
    setScheduleImpact('');
    setBuilderCost('');
    setClientPrice('');
    setPricingMode('manual');
    setPricingType('addition');
    setClientPriceError(null);
    setBuilderCostError(null);
    setInternalNote('');
    setRequiresClientApproval(true);
    setCoTarget('internal');
    setToUserId('');
    setUploadedFiles([]);
    setError('');
    setShowAssignPanel(false);
    setSubcontractors([]);
    setSubcontractorsLoading(false);
    if (initialProjectId) {
      setSelectedProjectId(initialProjectId);
    } else {
      setSelectedProjectId('');
      setSelectedProject(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  useEffect(() => {
    if (initialProjectId) setSelectedProjectId(initialProjectId);
  }, [initialProjectId]);

  // Load single project when not allowed to change
  useEffect(() => {
    if (!show || !selectedProjectId || allowProjectChange) return;
    let cancelled = false;
    setProjectLoading(true);
    fetchProjectWithClient(selectedProjectId).then((proj) => {
      if (cancelled) return;
      if (proj) setSelectedProject(proj);
      setProjectLoading(false);

    });
    return () => { cancelled = true; };
  }, [show, selectedProjectId, allowProjectChange]);

  // Map from hook when allowed to change
  useEffect(() => {
    if (!show || !allowProjectChange) return;
    if (selectProjects.length === 0) return;
    const match = selectProjects.find((p) => p.id === selectedProjectId);
    if (match) {
      const projectOption: ProjectOption = {
        id: match.id,
        project_name: match.project_name,
        project_code: match.id.slice(0, 6).toUpperCase(),
        site_address: match.site_address,
        status: match.status || 'active',
        client_user_id: match.client_user_id || null,
        client_name: match.client_name || null,
      };
      setSelectedProject(projectOption);

      if (match.client_user_id && !match.client_name) {
        let cancelled = false;
        (async () => {
          const { data: profile } = await supabase
            .from('jphph_profiles')
            .select('full_name, company_name')
            .eq('id', match.client_user_id)
            .maybeSingle();
          if (cancelled) return;
          const clientName = (profile?.full_name as string | null) || (profile?.company_name as string | null) || null;
          if (!cancelled) {
            setSelectedProject((prev) => prev ? { ...prev, client_name: clientName } : prev);
          }
        })();
        return () => { cancelled = true; };
      }
    } else if (!selectedProjectId) {
      setSelectedProject(null);
    }
  }, [show, allowProjectChange, selectProjects, selectedProjectId]);

  // Load subcontractors when project changes
  useEffect(() => {
    if (!show || !selectedProjectId) return;
    let cancelled = false;
    setSubcontractorsLoading(true);
    loadSubcontractorsForProject(selectedProjectId).then((subs) => {
      if (cancelled) return;
      setSubcontractors(subs);
      if (subs.length === 1) setToUserId(subs[0].id);
      else setToUserId('');
      setSubcontractorsLoading(false);
    });
    return () => { cancelled = true; };
  }, [show, selectedProjectId]);

  // Suggest CO number when project is selected
  useEffect(() => {
    if (!show || !selectedProjectId) return;
    let cancelled = false;
    getSuggestedCoNumber(selectedProjectId).then(({ number }) => {
      if (!cancelled) setCoNumber((prev) => prev.trim() ? prev : number);
    });
    return () => { cancelled = true; };
  }, [show, selectedProjectId]);

  const handleFileUploadComplete = useCallback((records: UploadedFileRecord[]) => {
    setUploadedFiles((prev) => [...prev, ...records]);
    setShowFileUploader(false);
  }, []);

  const isFormValid = useMemo(() => {
    if (!selectedProjectId || !title.trim() || !coNumber.trim()) return false;
    if (!reason.trim()) return false;
    if (coTarget === 'internal' && !description.trim()) return false;
    if (coTarget === 'client' && !clientDescription.trim()) return false;
    if (coTarget !== 'internal' && (!currentFinish.trim() || !newFinish.trim())) return false;
    if (coTarget === 'subcontractor' && !toUserId) return false;
    if (coTarget === 'client' && !selectedProject?.client_user_id) return false;
    if (coTarget === 'client' && persistFormPricing(builderCost, clientPrice, pricingType, { clientRequired: true }).error) return false;
    return true;
  }, [selectedProjectId, title, coNumber, reason, description, clientDescription, coTarget, currentFinish, newFinish, toUserId, selectedProject, clientPrice, builderCost, pricingType]);

  const getValidationMessage = useMemo(() => {
    if (!selectedProjectId) return 'Please select a project first.';
    if (!coNumber.trim()) return 'Enter a CO number.';
    if (!title.trim()) return 'Enter a change order title.';
    if (!reason.trim()) return 'Enter the reason for change.';
    if (coTarget === 'internal' && !description.trim()) return 'Enter an internal description.';
    if (coTarget === 'client' && !clientDescription.trim()) return 'Enter a client-visible description.';
    if (coTarget !== 'internal' && (!currentFinish.trim() || !newFinish.trim())) return 'Enter current and new finish descriptions.';
    if (coTarget === 'subcontractor' && !toUserId) {
      if (subcontractors.length === 0) return 'Assign and select a subcontractor before sending.';
      return 'Select a subcontractor recipient.';
    }
    if (coTarget === 'client' && !selectedProject?.client_user_id) {
      return 'No client is assigned to this project. Assign a client before requesting client approval.';
    }
    const newCoPriceErr = persistFormPricing(builderCost, clientPrice, pricingType, {
      clientRequired: coTarget === 'client',
    }).error;
    if (newCoPriceErr) {
      return newCoPriceErr;
    }
    return '';
  }, [selectedProjectId, title, coNumber, reason, description, clientDescription, coTarget, currentFinish, newFinish, toUserId, subcontractors.length, selectedProject, clientPrice, builderCost, pricingType]);

  const handleClose = useCallback(() => {
    resetForm();
    onClose();
  }, [onClose]);

  const handleSubmit = useCallback(async () => {


    if (!user || !isFormValid || !selectedProjectId) {
      setError(getValidationMessage || 'Please fill in all required fields.');
      return;
    }

    setSaving(true);
    setError('');

    let status = 'draft';
    if (coTarget === 'client') status = 'pending_client_approval';
    if (coTarget === 'subcontractor') status = 'in_progress';

    const duplicateError = await validateCoNumberUnique(selectedProjectId, coNumber);
    if (duplicateError) {
      setError(duplicateError);
      setSaving(false);
      return;
    }

    const { co_number, co_sequence } = await resolveCoNumberFields(selectedProjectId, coNumber);

    const pricingResult = persistFormPricing(builderCost, clientPrice, pricingType, {
      clientRequired: coTarget === 'client' || requiresClientApproval,
    });
    if (pricingResult.error) {
      setClientPriceError(pricingResult.error);
      setError(pricingResult.error);
      setSaving(false);
      return;
    }
    setClientPriceError(null);
    setBuilderCostError(null);

    const payload: Record<string, unknown> = {
      project_id: selectedProjectId,
      title: title.trim(),
      co_number,
      co_sequence,
      description: description.trim() || null,
      client_description: clientDescription.trim() || null,
      reason: reason.trim() || null,
      current_finish_description: currentFinish.trim() || null,
      new_finish_description: newFinish.trim() || null,
      schedule_impact: scheduleImpact.trim() || null,
      builder_cost: pricingResult.pricing.builder_cost,
      client_price: pricingResult.pricing.client_price,
      // Persist Auto vs Manual explicitly. Legacy rows stay NULL/unknown.
      pricing_method: pricingMode === 'auto' ? 'auto_excl' : 'manual_incl',
      internal_note: internalNote.trim() || null,
      status,
      requires_client_approval: coTarget === 'client',
      client_visible: coTarget === 'client' || status !== 'draft',
      created_by: user.id,
    };



    let { data: coData, error: coErr } = await supabase
      .from('jphph_change_orders')
      .insert(payload)
      .select()
      .maybeSingle();

    // Safe deployment ordering: creation still works if UI reaches production just before the migration.
    if (coErr && /pricing_method|schema cache|column/i.test(coErr.message || '')) {
      const compatibilityPayload = { ...payload };
      delete compatibilityPayload.pricing_method;
      ({ data: coData, error: coErr } = await supabase
        .from('jphph_change_orders')
        .insert(compatibilityPayload)
        .select()
        .maybeSingle());
    }

    if (coErr) {
      setError(coErr.message);
      setSaving(false);
      return;
    }

    const changeOrderId = coData?.id;


    // Link files
    if (changeOrderId && uploadedFiles.length > 0) {
      for (const f of uploadedFiles) {
        await supabase.from('jphph_project_files')
          .update({ change_order_id: changeOrderId })
          .eq('id', f.id);
      }
    }

    // Notification for client
    if (coTarget === 'client' && selectedProject?.client_user_id && changeOrderId) {
      await supabase.from('jphph_notifications').insert({
        user_id: selectedProject.client_user_id,
        project_id: selectedProjectId,
        notification_type: 'change_order',
        title: `Change Order requires your approval: ${title.trim()}`,
        body: (clientDescription.trim() || description.trim() || '').slice(0, 200),
        target_role: 'client',
        link_path: '/client/instructions',
        related_table: 'jphph_change_orders',
        related_id: changeOrderId,
        is_read: false,
      });
    }

    // Notification for subcontractor
    if (coTarget === 'subcontractor' && toUserId && changeOrderId) {
      await supabase.from('jphph_notifications').insert({
        user_id: toUserId,
        project_id: selectedProjectId,
        notification_type: 'change_order',
        title: `New Change Order: ${title.trim()}`,
        body: (description.trim() || '').slice(0, 200),
        target_role: 'subcontractor',
        link_path: '/subcontractor/instructions',
        related_table: 'jphph_change_orders',
        related_id: changeOrderId,
        is_read: false,
      });
    }

    setSaving(false);
    resetForm();
    handleClose();
    emitChangeOrdersSync(selectedProjectId);
    onSuccess?.();
  }, [
    user, title, coNumber, description, clientDescription, reason, currentFinish, newFinish,
    scheduleImpact, builderCost, clientPrice, internalNote, selectedProjectId,
    coTarget, toUserId, selectedProject, uploadedFiles, handleClose, onSuccess,
    isFormValid, getValidationMessage,
  ]);

  function resetForm() {
    setTitle('');
    setCoNumber('');
    setDescription('');
    setClientDescription('');
    setReason('');
    setCurrentFinish('');
    setNewFinish('');
    setScheduleImpact('');
    setBuilderCost('');
    setClientPrice('');
    setPricingMode('manual');
    setPricingType('addition');
    setClientPriceError(null);
    setBuilderCostError(null);
    setInternalNote('');
    setRequiresClientApproval(true);
    setCoTarget('internal');
    setToUserId('');
    setUploadedFiles([]);
    setError('');
    setShowAssignPanel(false);
    if (!initialProjectId) setSelectedProjectId('');
  }

  if (!show) return null;

  const senderName = user?.user_metadata?.full_name || 'Management';

  const getRecipientContextDisplay = (): string => {
    if (coTarget === 'internal') return 'Internal (Draft)';
    if (coTarget === 'client') {
      if (!selectedProject?.client_user_id) return 'No client assigned';
      return `Client: ${selectedProject.client_name || 'Assigned Client'}`;
    }
    if (toUserId) {
      const sub = subcontractors.find((s) => s.id === toUserId);
      return sub ? `Subcontractor: ${sub.full_name || sub.company_name || sub.email || 'Unknown'}` : 'Select subcontractor';
    }
    return 'Subcontractor not selected';
  };

  const getStatusContextDisplay = (): string => {
    if (coTarget === 'internal') return 'Draft';
    if (coTarget === 'client') {
      if (!selectedProject?.client_user_id) return 'Internal only available until a client is assigned';
      return 'Pending Client Approval';
    }
    return 'In Progress';
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-end lg:items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={handleClose}></div>
      <div className="relative bg-white w-full max-w-full lg:max-w-lg rounded-t-3xl lg:rounded-3xl overflow-hidden max-h-[92vh] flex flex-col" style={{ boxSizing: 'border-box' }}>
        {/* Header */}
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between flex-shrink-0">
          <h3 className="text-[#1A1A1A] font-bold text-base">New Change Order</h3>
          <button onClick={handleClose} className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100 cursor-pointer">
            <i className="ri-close-line text-[#374151]"></i>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4 min-h-0">
          {/* Project & Recipients */}
          <div className="space-y-3">
            <p className="text-[10px] font-bold text-[#9CA3AF] uppercase tracking-wider">Project & Recipients</p>

            {/* Project Selector */}
            <div>
              <label className="block text-[#555555] text-xs font-semibold mb-1.5">Project *</label>
              {allowProjectChange ? (
                <div className="space-y-1">
                  <select
                    value={selectedProjectId}
                    onChange={(e) => {
                      const newId = e.target.value;
                      const match = selectProjects.find((p) => p.id === newId);

                      setSelectedProjectId(newId);
                    }}
                    disabled={selectProjectsLoading}
                    className="w-full min-w-0 max-w-full bg-white border border-[#E0DDD8] rounded-xl px-4 py-3 text-sm text-[#1A1A1A] cursor-pointer disabled:opacity-60"
                    style={{ boxSizing: 'border-box' }}
                  >
                    <option value="">Select a project...</option>
                    {selectProjects.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.project_name}{p.site_address ? ` — ${p.site_address}` : ''}
                      </option>
                    ))}
                  </select>
                  {selectProjectsLoading && (
                    <div className="flex items-center gap-1.5 text-[#9CA3AF] text-[10px]">
                      <i className="ri-loader-4-line animate-spin"></i>
                      Loading projects...
                    </div>
                  )}
                  {selectProjectsError && (
                    <div className="flex items-center gap-1.5 text-[#EF4444] text-[10px]">
                      <i className="ri-error-warning-line"></i>
                      {selectProjectsError}
                      <button onClick={refreshSelectProjects} className="underline cursor-pointer whitespace-nowrap">Retry</button>
                    </div>
                  )}
                  {selectProjects.length === 0 && !selectProjectsLoading && !selectProjectsError && (
                    <div className="flex items-center gap-1.5 text-[#9CA3AF] text-[10px]">
                      <i className="ri-information-line"></i>
                      No projects found. If projects exist, check access permissions.
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex items-center gap-2 bg-[#F9FAFB] rounded-xl px-4 py-3 border border-gray-100">
                  <div className="w-8 h-8 rounded-lg bg-[#E8621A]/10 flex items-center justify-center flex-shrink-0">
                    <i className="ri-building-line text-[#E8621A] text-sm"></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-[#1A1A1A] truncate">
                      {selectedProject?.project_name || (projectLoading ? 'Loading project...' : 'Unknown project')}
                    </p>
                    {selectedProject?.project_code && (
                      <p className="text-[10px] text-[#9CA3AF]">{selectedProject.project_code}</p>
                    )}
                  </div>
                  <span className="text-[10px] font-bold text-[#E8621A] bg-[#FEF0E7] px-2 py-0.5 rounded-full flex-shrink-0">
                    {selectedProject?.status || '—'}
                  </span>
                </div>
              )}
            </div>

            {/* Project Summary */}
            {selectedProject && (
              <div className="bg-[#F9FAFB] rounded-xl p-3 border border-gray-100 space-y-1.5">
                <div className="flex items-center gap-2">
                  <i className="ri-map-pin-line text-[#9CA3AF] text-xs"></i>
                  <span className="text-xs text-[#374151]">{selectedProject.site_address || 'No address'}</span>
                </div>
                {selectedProject.client_name && (
                  <div className="flex items-center gap-2">
                    <i className="ri-user-line text-[#9CA3AF] text-xs"></i>
                    <span className="text-xs text-[#374151]">Client: {selectedProject.client_name}</span>
                  </div>
                )}
              </div>
            )}

            {/* Target Recipient */}
            <div>
              <label className="block text-[#555555] text-xs font-semibold mb-1.5">Target Recipient</label>
              <div className="flex gap-2">
                {([
                  { value: 'internal' as CoTarget, label: 'Internal Only', icon: 'ri-lock-line' },
                  { value: 'subcontractor' as CoTarget, label: 'Send to Subcontractor', icon: 'ri-tools-line' },
                  { value: 'client' as CoTarget, label: 'Client Approval', icon: 'ri-user-line' },
                ]).map((t) => {
                  const isClientDisabled = t.value === 'client' && !selectedProject?.client_user_id;
                  return (
                    <button
                      key={t.value}
                      onClick={() => { if (!isClientDisabled) setCoTarget(t.value); }}
                      disabled={isClientDisabled}
                      title={isClientDisabled ? 'No client assigned to this project' : undefined}
                      className={`flex-1 flex items-center gap-1.5 py-2.5 rounded-xl text-[11px] font-semibold whitespace-nowrap border-2 transition-colors ${
                        coTarget === t.value
                          ? 'border-[#E8621A] text-[#E8621A] bg-[#FEF0E7]/30'
                          : isClientDisabled
                            ? 'border-gray-100 text-gray-300 bg-gray-50 cursor-not-allowed'
                            : 'border-gray-100 text-[#374151] bg-gray-50 cursor-pointer'
                      }`}
                    >
                      <i className={`${t.icon} text-sm ${isClientDisabled ? 'text-gray-300' : ''}`}></i>
                      {t.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Subcontractor selector for CO when target is subcontractor */}
            {coTarget === 'subcontractor' && (
              <div className="space-y-2">
                {subcontractorsLoading ? (
                  <div className="flex items-center gap-2 bg-[#F9FAFB] rounded-xl px-3 py-2.5 border border-gray-100">
                    <i className="ri-loader-4-line animate-spin text-[#9CA3AF] text-sm"></i>
                    <span className="text-sm text-[#9CA3AF]">Loading subcontractors...</span>
                  </div>
                ) : subcontractors.length === 0 ? (
                  <div className="space-y-3">
                    <div className="bg-[#FEF0E7] rounded-xl p-3 border border-[#FDBA74]">
                      <div className="flex items-start gap-2">
                        <i className="ri-information-line text-[#F59E0B] text-sm flex-shrink-0 mt-0.5"></i>
                        <div className="flex-1">
                          <p className="text-xs text-[#92400E] font-semibold mb-1">No subcontractors assigned yet</p>
                          <p className="text-[10px] text-[#92400E]/80 mb-2">Save as Draft for now, or switch to Internal Only target, or assign a subcontractor.</p>
                          <div className="flex flex-wrap gap-2">
                            <button
                              onClick={() => setShowAssignPanel(true)}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#E8621A] text-white text-[11px] font-bold cursor-pointer whitespace-nowrap"
                            >
                              <i className="ri-user-add-line"></i>
                              Assign Subcontractor
                            </button>
                            <button
                              onClick={() => setCoTarget('internal')}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white text-[#92400E] text-[11px] font-bold cursor-pointer whitespace-nowrap border border-[#FDBA74]"
                            >
                              <i className="ri-draft-line"></i>
                              Switch to Draft
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                    {showAssignPanel && (
                      <AssignSubcontractorPanel
                        projectId={selectedProjectId}
                        onAssigned={(sub) => {
                          setSubcontractors((prev) => [...prev, sub]);
                          setToUserId(sub.id);
                          setShowAssignPanel(false);
                        }}
                        onCancel={() => setShowAssignPanel(false)}
                      />
                    )}
                  </div>
                ) : subcontractors.length === 1 ? (
                  <div className="flex items-center gap-2 bg-[#ECFDF5] rounded-xl px-3 py-2.5 border border-[#A5F3FC]">
                    <i className="ri-tools-line text-[#059669] text-sm"></i>
                    <span className="text-sm font-medium text-[#059669]">
                      {subcontractors[0].full_name || subcontractors[0].company_name || subcontractors[0].email || 'Subcontractor'}
                    </span>
                  </div>
                ) : (
                  <select
                    value={toUserId}
                    onChange={(e) => setToUserId(e.target.value)}
                    className="w-full bg-white border border-[#E0DDD8] rounded-xl px-4 py-3 text-sm text-[#1A1A1A] cursor-pointer"
                  >
                    <option value="">Select subcontractor...</option>
                    {subcontractors.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.full_name || s.company_name || s.email || 'Unknown'}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            )}

            {/* Client approval info */}
            {coTarget === 'client' && (
              <div>
                {selectedProject?.client_user_id ? (
                  <div className="flex items-center gap-2 bg-[#FEF0E7] rounded-xl px-3 py-2.5 border border-[#FDBA74]">
                    <i className="ri-user-line text-[#E8621A] text-sm"></i>
                    <span className="text-sm font-medium text-[#92400E]">
                      Client Approval Recipient: {selectedProject.client_name || 'Assigned Client'}
                    </span>
                  </div>
                ) : (
                  <div className="bg-[#FEF2F2] rounded-xl p-3 border border-[#FECACA] space-y-2">
                    <div className="flex items-start gap-2">
                      <i className="ri-error-warning-line text-[#EF4444] text-sm flex-shrink-0 mt-0.5"></i>
                      <p className="text-xs text-[#EF4444]">
                        No client is assigned to this project. Assign a client before requesting client approval.
                      </p>
                    </div>
                    {selectedProjectId && (
                      <button
                        onClick={() => {
                          handleClose();
                          navigate(`/management/projects/${selectedProjectId}/edit`);
                        }}
                        className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#E8621A] hover:underline cursor-pointer"
                      >
                        <i className="ri-user-add-line"></i>
                        Assign client to this project
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Context Block */}
            {selectedProject && (
              <div className="bg-[#1A1A1A] rounded-xl p-3 space-y-2">
                <p className="text-[10px] font-bold text-white/40 uppercase tracking-wider">Sending Context</p>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
                  <div>
                    <p className="text-[10px] text-white/40">From</p>
                    <p className="text-xs font-semibold text-white">{senderName}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-white/40">Project</p>
                    <p className="text-xs font-semibold text-white truncate">{selectedProject.project_name}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-white/40">To</p>
                    <p className="text-xs font-semibold text-white truncate">{getRecipientContextDisplay()}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-white/40">Type</p>
                    <p className="text-xs font-semibold text-[#E8621A]">Change Order</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-white/40">Status</p>
                    <p className="text-xs font-semibold text-white">{getStatusContextDisplay()}</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Change Order Details */}
          <div className="space-y-3">
            <p className="text-[10px] font-bold text-[#9CA3AF] uppercase tracking-wider">Change Order Details</p>

            <div>
              <label className="block text-[#555555] text-xs font-semibold mb-1.5">CO Number *</label>
              <input
                type="text"
                value={coNumber}
                onChange={(e) => setCoNumber(e.target.value)}
                onBlur={() => setCoNumber((prev) => (prev.trim() ? normalizeCoNumber(prev) : ''))}
                placeholder="CO-001"
                className="w-full bg-white border border-[#E0DDD8] rounded-xl px-4 py-3 text-sm text-[#1A1A1A] placeholder-[#CCCCCC]"
              />
            </div>

            <div>
              <label className="block text-[#555555] text-xs font-semibold mb-1.5">Change Order Title *</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. Bathroom vanity specification change"
                className="w-full bg-white border border-[#E0DDD8] rounded-xl px-4 py-3 text-sm text-[#1A1A1A] placeholder-[#CCCCCC]"
              />
            </div>

            <div>
              <label className="block text-[#555555] text-xs font-semibold mb-1.5">
                Internal Description {coTarget === 'internal' && <span className="text-[#EF4444]">*</span>}
                <span className="ml-1 text-[10px] font-normal text-[#9CA3AF]">(internal only)</span>
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe the change order in detail..."
                rows={2}
                className="w-full bg-white border border-[#E0DDD8] rounded-xl px-4 py-3 text-sm text-[#1A1A1A] placeholder-[#CCCCCC] resize-none"
              />
            </div>

            <div>
              <label className="block text-[#555555] text-xs font-semibold mb-1.5">
                Client Description {coTarget === 'client' && <span className="text-[#EF4444]">*</span>}
                <span className="ml-1 text-[10px] font-normal text-[#059669]">(client-visible)</span>
              </label>
              <textarea
                value={clientDescription}
                onChange={(e) => setClientDescription(e.target.value)}
                placeholder="Description that the client will see..."
                rows={2}
                className="w-full bg-white border border-[#E0DDD8] rounded-xl px-4 py-3 text-sm text-[#1A1A1A] placeholder-[#CCCCCC] resize-none"
              />
            </div>

            <div>
              <label className="block text-[#555555] text-xs font-semibold mb-1">Reason for Change *</label>
              <input
                type="text"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. Client requested upgraded tile specification"
                className="w-full bg-white border border-[#E0DDD8] rounded-xl px-4 py-2.5 text-sm text-[#1A1A1A] placeholder-[#CCCCCC]"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-[#555555] text-xs font-semibold mb-1">Current Finish *</label>
                <input
                  type="text"
                  value={currentFinish}
                  onChange={(e) => setCurrentFinish(e.target.value)}
                  placeholder="What was originally planned"
                  className="w-full bg-white border border-[#E0DDD8] rounded-xl px-4 py-2.5 text-sm text-[#1A1A1A] placeholder-[#CCCCCC]"
                />
              </div>
              <div>
                <label className="block text-[#555555] text-xs font-semibold mb-1">New Finish *</label>
                <input
                  type="text"
                  value={newFinish}
                  onChange={(e) => setNewFinish(e.target.value)}
                  placeholder="What is being changed to"
                  className="w-full bg-white border border-[#E0DDD8] rounded-xl px-4 py-2.5 text-sm text-[#1A1A1A] placeholder-[#CCCCCC]"
                />
              </div>
            </div>

            <div>
              <label className="block text-[#555555] text-xs font-semibold mb-1">Schedule Impact</label>
              <input
                type="text"
                value={scheduleImpact}
                onChange={(e) => setScheduleImpact(e.target.value)}
                placeholder="e.g. +3 days or No impact"
                className="w-full bg-white border border-[#E0DDD8] rounded-xl px-4 py-2.5 text-sm text-[#1A1A1A] placeholder-[#CCCCCC]"
              />
            </div>

            <ChangeOrderPricingFields
              idPrefix="new-co"
              pricingType={pricingType}
              onPricingTypeChange={(type) => {
                setPricingType(type);
                setClientPriceError(null);
                setBuilderCostError(null);
                setError('');
              }}
              pricingMode={pricingMode}
              onPricingModeChange={(mode) => {
                setPricingMode(mode);
                setClientPriceError(null);
                setError('');
              }}
              builderCost={builderCost}
              onBuilderCostChange={(value) => {
                setBuilderCost(value);
                setBuilderCostError(null);
                setError('');
              }}
              clientPrice={clientPrice}
              onClientPriceChange={(value) => {
                setClientPrice(value);
                setClientPriceError(null);
                setError('');
              }}
              clientPriceError={clientPriceError}
              builderCostError={builderCostError}
            />

            <div>
              <label className="block text-[#555555] text-xs font-semibold mb-1.5">
                Internal Note
                <span className="ml-1 text-[10px] font-normal text-[#9CA3AF]">(internal only)</span>
              </label>
              <textarea
                value={internalNote}
                onChange={(e) => setInternalNote(e.target.value)}
                placeholder="Any internal notes..."
                rows={2}
                className="w-full bg-white border border-[#E0DDD8] rounded-xl px-4 py-2.5 text-sm text-[#1A1A1A] placeholder-[#CCCCCC] resize-none"
              />
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="co_requires_client_approval"
                checked={requiresClientApproval}
                onChange={(e) => setRequiresClientApproval(e.target.checked)}
                className="w-4 h-4 accent-[#E8621A] cursor-pointer"
              />
              <label htmlFor="co_requires_client_approval" className="text-xs text-[#374151] cursor-pointer">
                Requires Client Approval
              </label>
            </div>
          </div>

          {/* Attachments */}
          <div className="space-y-2">
            <p className="text-[10px] font-bold text-[#9CA3AF] uppercase tracking-wider">Attachments</p>
            {uploadedFiles.length > 0 && (
              <ChangeOrderAttachmentList
                files={uploadedFiles.map((f) => ({
                  id: f.id,
                  file_url: f.file_url,
                  file_name: f.file_name,
                  file_type: f.file_type,
                  photo_type: f.photo_type ?? null,
                  caption: f.note || null,
                  is_client_visible: f.is_client_visible ?? null,
                  change_order_id: f.change_order_id ?? null,
                  created_at: f.created_at ?? null,
                  storage_bucket: f.storage_bucket ?? null,
                  storage_path: f.storage_path ?? null,
                }))}
                onDelete={(file) => {
                  void (async () => {
                    try {
                      await unlinkChangeOrderAttachment(file.id);
                    } catch {
                      // Keep the file off the new-CO list even if unlink fails.
                    }
                    setUploadedFiles((prev) => prev.filter((item) => item.id !== file.id));
                  })();
                }}
                onVisibilityUpdated={(fileId, next) => {
                  setUploadedFiles((prev) =>
                    prev.map((file) => (file.id === fileId ? { ...file, ...next } : file))
                  );
                }}
              />
            )}
            <button
              onClick={() => setShowFileUploader(true)}
              className="w-full py-2.5 rounded-xl border-2 border-dashed border-gray-200 text-[#9CA3AF] text-sm font-medium hover:border-[#E8621A] hover:text-[#E8621A] cursor-pointer transition-colors flex items-center justify-center gap-2"
            >
              <i className="ri-upload-2-line"></i>
              {uploadedFiles.length > 0 ? `${uploadedFiles.length} file(s) attached — Add more` : 'Attach photos or PDFs'}
            </button>
          </div>

          {/* Error */}
          {error && (
            <div className="flex items-start gap-2 bg-[#FEF2F2] rounded-xl p-3">
              <i className="ri-error-warning-line text-[#EF4444] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-[#EF4444] text-xs">{error}</p>
            </div>
          )}
        </div>

        {/* Submit */}
        <div className="px-5 py-4 border-t border-gray-100 flex-shrink-0 space-y-2">
          <button
            onClick={handleSubmit}
            disabled={saving || !isFormValid}
            className="w-full py-3.5 rounded-xl bg-[#E8621A] text-white text-sm font-bold cursor-pointer whitespace-nowrap disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {saving ? (
              <><i className="ri-loader-4-line animate-spin"></i>Saving...</>
            ) : (
              <>
                <i className={coTarget === 'internal' ? 'ri-draft-line' : coTarget === 'client' ? 'ri-user-line' : 'ri-send-plane-line'}></i>
                {coTarget === 'internal' ? 'Save as Draft' : coTarget === 'client' ? 'Request Client Approval' : 'Send Change Order'}
              </>
            )}
          </button>
          {!isFormValid && getValidationMessage && (
            <p className="text-center text-[10px] text-[#9CA3AF] mt-2">{getValidationMessage}</p>
          )}
        </div>
      </div>

      {/* File Uploader */}
      {showFileUploader && user && selectedProjectId && (
        <FileUploader
          projectId={selectedProjectId}
          currentUserId={user.id}
          open={showFileUploader}
          onClose={() => setShowFileUploader(false)}
          onUploadComplete={handleFileUploadComplete}
          accountType="management"
          category="change_order"
          allowMultiple={true}
          allowCamera={true}
          allowPdf={true}
          enablePerFileCaption={true}
          showVisibilitySelector={true}
          visibilityScope="management"
          bucket="change-order-photos"
          relatedType="change_order"
          title="Attach Change Order Files"
        />
      )}
    </div>
  );
}